import React, { useEffect, useRef, useState, useContext } from "react";
import { t } from "i18next";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExport,
  faChartLine,
  faTable,
  faInfoCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { ModalPopup, Popover, SafeRenderHtml } from "../common";
import { AnalyticsWidgetUiDetailsDto } from "../../types/analytics";
import {
  ComplexScoreOverview,
  HistogramChart,
  NetPromoterScore,
  PieChart,
} from "./charts";
import { HorizontalBarChart, VerticalBarChart } from "./charts/BarCharts";
import { ErrorBoundary } from "react-error-boundary";
import ChartError from "./charts/ChartError";
import AnalyticsFilterInputs from "../../types/analytics/AnalyticsFilterInputs";
import AnalyticsDataTable from "./tables/AnalyticsDataTable";
import { GroupedJourneys } from "../../types/analytics/FilterAvailableValues";
import { useAuth } from "react-oidc-context";
import analyticsCustomEndpointApi from "../../api/analytics/analyticsCustomEndpointApi";
import { fileHelper } from "../../helpers";
import NineBoxGrid from "./charts/nineBoxGrid/NineBoxGrid";
import { KeyValuePair } from "../../types/generic";
import { BarChartDrilldownValue } from "../../types/analytics/charts/BarChartDrilldownValue";
import NineBoxGridEditor from "./charts/nineBoxGridEditor/NineBoxGridEditor";
import { AnalyticsPageWidgetSummary } from "../../types/analytics/AnalyticsWidgetUiDetailsDto";
import MultiScoreTable from "./charts/multiscore/MultiScoreTable";
import UserContext from "../../state/UserContext";
import TinyLoader from "../loaders/TinyLoader";
import { useExportComponentAsImage } from "../../hooks/useExportComponentAsImage";
import UsageStatusOverview from "./usage/UsageStatusOverview";

interface ExportableChartAreaProps {
  children: React.ReactNode;
  className?: string;
}

const ExportableChartArea = React.forwardRef<
  HTMLDivElement,
  ExportableChartAreaProps
>((props, ref) => (
  <div ref={ref} className={props.className}>
    {props.children}
  </div>
));

interface AnalyticsWidgetProps {
  widgetDetails: AnalyticsWidgetUiDetailsDto;
  filters: AnalyticsFilterInputs;
  journeys: GroupedJourneys[];
  dynamicPageId?: number | undefined;
  /** This is used for widgets being able to see what else is on the same page, e.g. to refresh 9BG data from the 9BG editor */
  allWidgetsForPage: AnalyticsPageWidgetSummary[];
  onRequestOtherWidgetRefresh?: (
    dynamicWidgetId: number,
    onSuccess: () => void,
    onError: () => void,
  ) => void;
}

function AnalyticsWidget({
  widgetDetails,
  filters,
  journeys,
  dynamicPageId,
  allWidgetsForPage,
  onRequestOtherWidgetRefresh,
}: AnalyticsWidgetProps) {
  const auth = useAuth();
  const userContext = useContext(UserContext);
  const themeColor = `#${userContext.user.client.primaryHexColour}`;
  const api = new analyticsCustomEndpointApi(auth.user?.access_token);
  const widgetTitle = t(widgetDetails.widgetTitle);
  const { exportAsJPEG } = useExportComponentAsImage();

  const [displayMode, setDisplayMode] = useState<"CHART" | "TABLE">("CHART");
  const [showSubMenu, setShowSubMenu] = useState<boolean>(false);
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState<boolean>(false);
  const [isExportingImage, setIsExportingImage] = useState<boolean>(false);
  const [customTableDataApiParams, setCustomTableDataApiParams] = useState<
    KeyValuePair<string, string>[] | null
  >(null);
  const componentRef = useRef<HTMLDivElement>(null);
  const canShowTable =
    widgetDetails.tableApiEndpoint && widgetDetails.tableApiEndpoint.length > 0;

  // The buttons that are shown in the header of the widget
  let headerButtons: Array<"CHART" | "TABLE" | "EXPORT" | "CHART-AS-TABLE"> =
    [];
  if (widgetDetails.displayType == "NINE-BOX-GRID-EDITOR") {
    // Hide all buttons for the 9BG editor
    headerButtons = [];
  } else if (widgetDetails.displayType == "MULTI-SCORE-TABLE") {
    // CHART-AS-TABLE is used for widgets that display data in a table format by default
    // but need to maintain the "CHART" display mode as that's how the table gets rendered
    // (like it is a chart, not the normal generic table view)
    headerButtons = ["CHART-AS-TABLE", "EXPORT"];
  } else {
    // Default buttons for most widgets
    headerButtons = ["CHART"];
    // If a table API endpoint is provided, show the option to toggle between chart and table
    if (canShowTable) {
      headerButtons.push("TABLE");
    }
    // Add export button by default unless specifically excluded
    headerButtons.push("EXPORT");
  }

  useEffect(() => {
    // Reset the custom table data API params when the widget changes
    setCustomTableDataApiParams(null);
  }, [widgetDetails.widgetId]);

  useEffect(() => {
    // Reset the display mode when the widget gets manually refreshed via another widget
    // (otherwise the user could be in a table view and then the chart gets refreshed, so that table
    // needs refreshing, which will happen when they drilldown again)
    setDisplayMode("CHART");
  }, [widgetDetails.datasets]);

  useEffect(() => {
    if (isExportingImage) {
      // Do the actual image export after the state change has been applied to render any legends etc
      exportAsJPEG(componentRef, "analytics-chart.jpeg");
      // Hide the legends after the image has exported
      setIsExportingImage(false);
    }
  }, [isExportingImage, exportAsJPEG]);

  const triggerHelpModal = () => {
    setShowHelpModal(true);
  };

  const exportChartArea = () => {
    // Set the state value, which triggers the `useEffect` after, allowing the legends etc
    // to be rendered before generating the image
    setIsExportingImage(true);
  };

  const downloadExcelFile = () => {
    if (!widgetDetails.excelApiEndpoint) return;

    // Disable the export button to avoid batch clicking
    setIsDownloadingExcel(true);

    const onError = (error: any) => {
      // Re-enable the download button
      setIsDownloadingExcel(false);
      console.error("Unable to download file", error);
    };
    const onSuccess = () => {
      // Re-enable the download button
      setIsDownloadingExcel(false);
    };

    const filenameWithoutExtension = fileHelper.createFilenameFromString(
      widgetTitle,
      true,
    );

    api.downloadSpreadsheetFile(
      widgetDetails.excelApiEndpoint,
      filenameWithoutExtension,
      filters,
      onSuccess,
      onError,
    );
  };

  const onChartDrilldown = (
    drilldownValue: string,
    searchType: "EXACT" | "RANGE",
    /** For stacked bars, we need to know the bar outer value, and the item within the bar which was clicked, which is the normal `drilldownValue` */
    outerDrilldownValue?: string | undefined,
  ) => {
    setCustomTableDataApiParams([
      { key: "drilldownValue", value: drilldownValue },
      { key: "drilldownType", value: searchType },
      { key: "outerDrilldownValue", value: outerDrilldownValue || "" },
    ]);
    setDisplayMode("TABLE");
  };

  const onNineBoxGridDrilldown = (boxNumber: number) => {
    setCustomTableDataApiParams([
      { key: "boxNumber", value: boxNumber.toString() },
    ]);
    setDisplayMode("TABLE");
  };

  const displayModeButtonClassNames =
    "px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 border-[1px] border-gray-200 text-gray-400 hover:text-gray-600 disabled:text-gray-700 disabled:cursor-not-allowed disabled:bg-gray-200";

  // Get the help content, if there is any
  let helpContent: string | null = null;
  if (widgetDetails.widgetHelp && widgetDetails.widgetHelp.length > 0) {
    if (widgetDetails.widgetType === "DYNAMIC") {
      helpContent = widgetDetails.widgetHelp;
    } else {
      const translatedHelpContent = t(widgetDetails.widgetHelp);
      if (translatedHelpContent !== widgetDetails.widgetHelp) {
        helpContent = translatedHelpContent;
      }
    }
  }

  const handleBarChartClick = (item: BarChartDrilldownValue) => {
    onChartDrilldown(
      item.value.toString(),
      "EXACT",
      item.stackedOuterValue?.toString(),
    );
  };

  // Get the nine box grid widget, if there is one.
  // In theory there could be more than one, but it's unlikely.
  // This code here exists to pass into a NineBoxGridEditor widget, so the user
  // can refresh the 9BG data in another widget after editing the underlying data.
  const nineBoxGridWidget = allWidgetsForPage.find(
    (x) => x.displayType === "NINE-BOX-GRID",
  );

  return (
    <div
      data-widget-id={widgetDetails.widgetId}
      className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden"
    >
      <div className="p-4">
        <div className="flex flex-col lg:flex-row items-start">
          <div className="grow">
            <div className="flex items-center gap-2 mb-1.5">
              <h3 className="text-xl font-semibold">{widgetTitle}</h3>

              {helpContent && (
                <>
                  <button
                    onClick={triggerHelpModal}
                    className="primary-color p-1 flex items-center group cursor-help"
                  >
                    <FontAwesomeIcon size="sm" icon={faInfoCircle} />
                    <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-in-out text-sm ml-0 group-hover:ml-1 whitespace-nowrap">
                      More info
                    </span>
                  </button>
                  <ModalPopup
                    title={widgetTitle}
                    isOpen={showHelpModal}
                    onOpenChange={setShowHelpModal}
                    width="SMALL"
                  >
                    <SafeRenderHtml
                      htmlText={helpContent}
                      containerClassName="analytics-widget-help-container"
                    />
                  </ModalPopup>
                </>
              )}
            </div>
            <div className="hidden md:block text-gray-500 text-sm">
              {t(widgetDetails.widgetSubTitle)}
            </div>
          </div>

          {headerButtons.length > 0 && (
            <div className="hidden flex-none md:flex items-start lg:ml-4 mt-4 lg:mt-0">
              <div className="inline-flex rounded-md shadow-sm">
                {headerButtons.indexOf("CHART") !== -1 && (
                  <button
                    className={cx(
                      "px-4 py-2 text-sm font-medium bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed disabled:bg-gray-50 transition-all duration-150",
                      "rounded-l-md",
                      displayMode === "CHART" && "relative z-10",
                    )}
                    onClick={() => setDisplayMode("CHART")}
                    disabled={displayMode === "CHART"}
                    title="Show chart"
                    style={
                      displayMode === "CHART"
                        ? {
                            backgroundColor: `${themeColor}10`,
                            borderColor: `${themeColor}`,
                            color: themeColor,
                          }
                        : {}
                    }
                  >
                    <FontAwesomeIcon
                      icon={faChartLine}
                      size="xs"
                      className="mr-1.5"
                    />
                    Chart
                  </button>
                )}
                {headerButtons.indexOf("CHART-AS-TABLE") !== -1 && (
                  <button
                    className={cx(
                      "px-4 py-2 text-sm font-medium bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed disabled:bg-gray-50 transition-all duration-150",
                      "rounded-l-md",
                      displayMode === "CHART" && "relative z-10",
                    )}
                    onClick={() => setDisplayMode("CHART")}
                    disabled={displayMode === "CHART"}
                    title="Show data"
                    style={
                      displayMode === "CHART"
                        ? {
                            backgroundColor: `${themeColor}10`,
                            borderColor: `${themeColor}`,
                            color: themeColor,
                          }
                        : {}
                    }
                  >
                    <FontAwesomeIcon
                      icon={faTable}
                      size="xs"
                      className="mr-1.5"
                    />
                    Data
                  </button>
                )}
                {headerButtons.indexOf("TABLE") !== -1 && (
                  <button
                    className={cx(
                      "px-4 py-2 text-sm font-medium bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed disabled:bg-gray-50 transition-all duration-150 -ml-px",
                      displayMode === "TABLE" && "relative z-10",
                    )}
                    onClick={() => setDisplayMode("TABLE")}
                    disabled={displayMode === "TABLE"}
                    title="Show data"
                    style={
                      displayMode === "TABLE"
                        ? {
                            backgroundColor: `${themeColor}10`,
                            borderColor: `${themeColor}`,
                            color: themeColor,
                          }
                        : {}
                    }
                  >
                    <FontAwesomeIcon
                      icon={faTable}
                      size="xs"
                      className="mr-1.5"
                    />
                    Data
                  </button>
                )}
                {headerButtons.indexOf("EXPORT") !== -1 && (
                  <Popover
                    title="Export..."
                    openState={showSubMenu}
                    onOpenStateChange={setShowSubMenu}
                    triggerElement={
                      <button
                        className={cx(
                          "px-4 py-2 text-sm font-medium bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 hover:text-gray-900 transition-all duration-150 -ml-px",
                          "rounded-r-md",
                        )}
                      >
                        <FontAwesomeIcon
                          icon={faFileExport}
                          size="xs"
                          className="mr-1.5"
                        />
                        Export
                      </button>
                    }
                  >
                    <ul className="py-1">
                      {widgetDetails.excelApiEndpoint &&
                        widgetDetails.excelApiEndpoint.length > 0 && (
                          <li>
                            <button
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors duration-150 flex items-center gap-2"
                              onClick={downloadExcelFile}
                              disabled={isDownloadingExcel}
                            >
                              Excel file
                              {isDownloadingExcel && (
                                <TinyLoader
                                  size="sm"
                                  classNames="primary-colour"
                                />
                              )}
                            </button>
                          </li>
                        )}
                      {displayMode === "CHART" && (
                        <li>
                          <button
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                            onClick={exportChartArea}
                          >
                            Image file
                          </button>
                        </li>
                      )}
                    </ul>
                  </Popover>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="p-6">
        {displayMode === "CHART" && (
          <ErrorBoundary FallbackComponent={ChartError}>
            <ExportableChartArea ref={componentRef}>
              {widgetDetails.widgetType === "USAGE-SUMMARY" && (
                <UsageStatusOverview widget={widgetDetails} />
              )}
              {widgetDetails.displayType === "PIE" && (
                <PieChart
                  widget={widgetDetails}
                  onSegmentClick={(value) => onChartDrilldown(value, "EXACT")}
                />
              )}
              {widgetDetails.displayType === "HORIZONTAL-BAR" && (
                <HorizontalBarChart
                  widget={widgetDetails}
                  isExportingImage={isExportingImage}
                  onBarClick={handleBarChartClick}
                />
              )}
              {widgetDetails.displayType === "VERTICAL-BAR" && (
                <VerticalBarChart
                  widget={widgetDetails}
                  isExportingImage={isExportingImage}
                  onBarClick={handleBarChartClick}
                />
              )}
              {widgetDetails.displayType === "HISTOGRAM" && (
                <HistogramChart
                  widget={widgetDetails}
                  isExportingImage={isExportingImage}
                  onBarClick={(item) =>
                    onChartDrilldown(item.value.toString(), "RANGE")
                  }
                />
              )}
              {widgetDetails.displayType === "NET-PROMOTER-SCORE" && (
                <NetPromoterScore
                  widget={widgetDetails}
                  onDrilldown={(score) =>
                    onChartDrilldown(score.toString(), "EXACT")
                  }
                />
              )}
              {widgetDetails.displayType === "NINE-BOX-GRID" && (
                <NineBoxGrid
                  widget={widgetDetails}
                  onDrilldown={onNineBoxGridDrilldown}
                />
              )}
              {widgetDetails.displayType === "COMPLEX-SCORE-OVERVIEW" && (
                <ComplexScoreOverview widget={widgetDetails} />
              )}
              {widgetDetails.displayType === "NINE-BOX-GRID-EDITOR" && (
                <NineBoxGridEditor
                  widget={widgetDetails}
                  analyticsPageId={dynamicPageId!}
                  filters={filters}
                  nineBoxGridWidgetId={
                    nineBoxGridWidget ? nineBoxGridWidget.dynamicWidgetId : null
                  }
                  onRequestOtherWidgetRefresh={onRequestOtherWidgetRefresh}
                />
              )}
              {widgetDetails.displayType === "MULTI-SCORE-TABLE" && (
                <MultiScoreTable
                  widget={widgetDetails}
                  analyticsPageId={dynamicPageId!}
                  filters={filters}
                />
              )}
            </ExportableChartArea>
          </ErrorBoundary>
        )}
        {displayMode === "TABLE" && (
          <AnalyticsDataTable
            filters={filters}
            apiUrl={widgetDetails.tableApiEndpoint!}
            journeys={journeys}
            legendItems={widgetDetails.legendItems}
            otherApiParams={customTableDataApiParams}
          />
        )}
      </div>
    </div>
  );
}

export default AnalyticsWidget;
